import "./App.css";
import { useAppDispatch, useAppSelector } from "./state/redux/hooks";
import {
  selectCollectionsSidebarOpen,
  selectMe,
  selectTimeSidebarOpen,
  setCollectionsSidebarOpen,
  setTimeSidebarOpen,
} from "./state/redux/slices/ui";

import { GoogleLogin, useGoogleOneTapLogin } from "@react-oauth/google";
import { authorize } from "./common/auth";
import CollectionsListener from "./components/utils/CollectionsListener";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
} from "./components/ui/sidebar";
import CollectionsList from "./components/ui/hg/CollectionsList";
import StacksVertical from "./components/ui/hg/StacksVertical";
import UserMenu from "./components/ui/hg/UserMenu";
import { Outlet, useLocation, useNavigate } from "react-router";
import Header from "./components/ui/hg/Header";
import BrandMark from "./components/ui/hg/BrandMark";
import ThemeToggle from "./components/ui/hg/ThemeToggle";
import SpaceSwitcher, {
  SpaceFollowBanner,
} from "./components/ui/hg/SpaceSwitcher";
import CollectionsSidebarController from "./components/ui/hg/CollectionsSidebarController";
import TimeSidebarController from "./components/ui/hg/TimeSidebarController";
import ThemeSync from "./components/utils/ThemeSync";
import { Toaster } from "./components/ui/sonner";
import getGlobalStacks from "./state/globalStacks";
import { useAppChrome } from "./common/chrome";
import { useEffect } from "react";

const HistorySync = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.state?.stacks) {
      // Seed the initial history entry with the current stacks state so that
      // pressing Back all the way to entry #1 correctly restores stacks
      // (without this, entry #1 has no state and back-navigation silently does nothing)
      // Whatever else the link put in the state survives the seeding — the
      // timeline's dive-in link marks its arrival with `keepRange`.
      navigate(location.pathname, {
        state: { ...location.state, stacks: getGlobalStacks().save() },
        replace: true,
      });
    } else if (getGlobalStacks().save() !== location.state.stacks) {
      // Avoid reloading if already in sync (prevents circular update loop)
      getGlobalStacks().load(location.state.stacks);
    }
  }, [location.state, location.pathname, navigate]);

  return null;
};

function App() {
  const me = useAppSelector(selectMe);
  const dispatch = useAppDispatch();
  // Desktop open state is held in Redux so the header, which is outside both
  // providers, can read and toggle it. See useSidebarToggle.
  const timeOpen = useAppSelector(selectTimeSidebarOpen);
  const collectionsOpen = useAppSelector(selectCollectionsSidebarOpen);
  const chrome = useAppChrome();

  useGoogleOneTapLogin({
    use_fedcm_for_prompt: true,
    cancel_on_tap_outside: true,
    auto_select: false,
    onSuccess: authorize,
    onError: () => {
      console.error("One-tap login failed");
    },
    disabled: !!me,
  });

  return (
    <div className="flex h-full w-full overflow-hidden">
      <ThemeSync />
      <Toaster position="bottom-right" />
      {/* Nothing to listen to on the landing page, and asking would be a
          request for the collections of nobody -- /collections with no user_id
          and no session is a guaranteed 401. */}
      {!chrome.isLanding && <CollectionsListener />}
      <HistorySync />
      {/* Not merely collapsed: a route with no time in it (a record's page, a
          collection's notes) drops the panel, and with it the ⌘B shortcut and
          the header button that would otherwise open something the page has no
          use for. See useAppChrome. */}
      {chrome.showTimeSidebar && (
        <div>
          <SidebarProvider
            storageKey="sidebar_time"
            shortcutKey="b"
            open={timeOpen}
            onOpenChange={(open) => dispatch(setTimeSidebarOpen(open))}
          >
            <TimeSidebarController />
            <Sidebar side="left">
              <SidebarHeader className="flex h-[55px] border-b flex-row items-center gap-[10px] px-[18px]">
                <BrandMark />
                <ThemeToggle />
              </SidebarHeader>
              <SidebarContent className="h-full">
                <StacksVertical />
              </SidebarContent>
              <SidebarFooter>{/* <RecordNew /> */}</SidebarFooter>
            </Sidebar>
          </SidebarProvider>
        </div>
      )}

      <div className="h-full min-w-0 grow flex flex-col">
        {chrome.showHeader && <Header />}
        <div className="flex w-full grow min-h-0 gap-2">
          <Outlet />
        </div>
      </div>

      {chrome.showCollectionsSidebar && (
        <div>
          <SidebarProvider
            storageKey="sidebar_collections"
            shortcutKey="j"
            open={collectionsOpen}
            onOpenChange={(open) => dispatch(setCollectionsSidebarOpen(open))}
          >
            <CollectionsSidebarController />
            <Sidebar side="right">
              {/* The band answers "whose collections am I reading?". The account
                lives in the footer, so only one avatar sits at the top. */}
              <SidebarHeader className="h-[55px] border-b flex flex-row items-center px-2">
                <SpaceSwitcher />
              </SidebarHeader>
              <SidebarContent className="noscroll">
                <SpaceFollowBanner />
                <CollectionsList />
              </SidebarContent>
              <SidebarFooter className="border-t">
                <SidebarMenu>
                  <SidebarMenuItem>
                    {me ? (
                      <UserMenu />
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <GoogleLogin auto_select onSuccess={authorize} />
                        {/* <Link
                        to="/login"
                        className="text-muted-foreground hover:text-foreground text-xs"
                      >
                        Sign in with a password
                      </Link> */}
                      </div>
                    )}
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarFooter>
            </Sidebar>
          </SidebarProvider>
        </div>
      )}
    </div>
  );
}

export default App;
